package com.rosario.juridico.marketplace.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.navigation.NavController
import com.rosario.juridico.domain.PerfilAbogadoDataModel
import com.rosario.juridico.marketplace.model.Consulta
import com.rosario.juridico.marketplace.model.NivelVerificacion
import com.rosario.juridico.marketplace.viewmodel.MarketplaceViewModel
import com.rosario.juridico.marketplace.viewmodel.UiState
import com.rosario.juridico.navigation.Routes
import com.rosario.juridico.marketplace.viewmodel.ConsultasFeedViewModel

private val AzulCliente = Color(0xFF1A7FD4)
private val VerdeAbogado = Color(0xFF1D9E75)
private val AzulB2B = Color(0xFF283593)

private val ListaSubtiposB2B = listOf("Corresponsalía", "Derivación de Caso", "Interconsulta / Dictamen")
private val ListaModalidadesHonorarios = listOf("50 / 50", "Honorario Fijo JUS", "A Convenir")

@OptIn(ExperimentalLayoutApi::class)
@Composable
fun PublicarConsultaScreen(
    navController: NavController,
    viewModel: MarketplaceViewModel,
    feedViewModel: ConsultasFeedViewModel,
    tipoConsultaParam: String? = null
) {
    val filtroTipoConsultaState by feedViewModel.filtroTipoConsulta.collectAsState()
    val isB2BMode = (tipoConsultaParam ?: filtroTipoConsultaState) == "colegiado"

    var descripcion  by remember { mutableStateOf("") }
    var urgencia     by remember { mutableStateOf("Media") }
    var ubicacion    by remember { mutableStateOf("Rosario") }
    var telefono     by remember { mutableStateOf("") }

    // Campos B2B
    var subtipoB2B          by remember { mutableStateOf("Corresponsalía") }
    var jurisdiccionDestino by remember { mutableStateOf("Santa Fe Capital") }
    var modalidadHonorarios by remember { mutableStateOf("50 / 50") }

    val isLoggedIn         by viewModel.isLoggedIn.collectAsState()
    val isEmailVerified    by viewModel.isEmailVerified.collectAsState()
    val destacados         by viewModel.abogadosDestacados.collectAsState()

    val isVerificadoNivel3 = feedViewModel.isUsuarioVerificadoNivel3()
    val publicarState      by feedViewModel.publicarConsultaState.collectAsState()

    LaunchedEffect(Unit) {
        viewModel.refreshAuthState()
        viewModel.loadAbogadosDestacados()
        viewModel.loadCurrentUserAbogado()
        viewModel.cargarPerfilAbogado()
    }

    Column(modifier = Modifier.fillMaxSize()) {

        MktHeader(
            titulo    = if (isB2BMode) "Nueva Interconsulta (Red B2B)" else "Publicar Consulta",
            subtitulo = if (isB2BMode) "Corresponsalías y Colaboración entre Colegas" else "Describí tu caso para recibir presupuestos",
            onBack    = { navController.popBackStack() }
        )

        Column(
            modifier = Modifier
                .fillMaxSize()
                .verticalScroll(rememberScrollState())
                .padding(20.dp),
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {

            if (!isB2BMode && destacados.isNotEmpty()) {
                Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        Icon(Icons.Default.Verified, null, tint = VerdeAbogado, modifier = Modifier.size(16.dp))
                        Text(
                            "Abogados Premium online",
                            style = MaterialTheme.typography.labelLarge.copy(fontWeight = FontWeight.Bold),
                            color = VerdeAbogado
                        )
                    }
                    LazyRow(
                        horizontalArrangement = Arrangement.spacedBy(12.dp),
                        contentPadding = PaddingValues(vertical = 4.dp)
                    ) {
                        items(destacados) { abog ->
                            val iniciales = "${abog.nombre} ${abog.apellido.take(1)}."
                            Column(
                                horizontalAlignment = Alignment.CenterHorizontally,
                                modifier = Modifier.width(70.dp)
                            ) {
                                Box(
                                    modifier = Modifier
                                        .size(50.dp)
                                        .clip(CircleShape)
                                        .background(VerdeAbogado.copy(alpha = 0.1f))
                                        .border(1.dp, VerdeAbogado.copy(alpha = 0.3f), CircleShape),
                                    contentAlignment = Alignment.Center
                                ) {
                                    Text(
                                        text = abog.nombre.take(1) + abog.apellido.take(1),
                                        style = MaterialTheme.typography.titleSmall,
                                        color = VerdeAbogado,
                                        fontWeight = FontWeight.Bold
                                    )
                                }
                                Spacer(Modifier.height(4.dp))
                                Text(
                                    text = iniciales,
                                    style = MaterialTheme.typography.labelSmall,
                                    maxLines = 1,
                                    textAlign = TextAlign.Center,
                                    color = MaterialTheme.colorScheme.onSurface
                                )
                            }
                        }
                    }
                    HorizontalDivider(
                        modifier = Modifier.padding(top = 8.dp),
                        color = MaterialTheme.colorScheme.outline.copy(alpha = 0.1f)
                    )
                }
            }

            if (!isLoggedIn) {
                Card(
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(16.dp),
                    colors = CardDefaults.cardColors(containerColor = AzulCliente.copy(alpha = 0.05f))
                ) {
                    Column(
                        modifier = Modifier.padding(16.dp),
                        verticalArrangement = Arrangement.spacedBy(12.dp),
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        Icon(
                            Icons.Default.AccountCircle, 
                            null, 
                            tint = AzulCliente, 
                            modifier = Modifier.size(48.dp)
                        )
                        Text(
                            "Inicia Sesión para Continuar",
                            fontWeight = FontWeight.Bold,
                            color = AzulCliente,
                            textAlign = TextAlign.Center
                        )
                        Button(
                            onClick = { navController.navigate(Routes.login(register = false)) },
                            modifier = Modifier.fillMaxWidth().height(50.dp),
                            colors = ButtonDefaults.buttonColors(containerColor = AzulCliente),
                            shape = RoundedCornerShape(12.dp)
                        ) {
                            Text("Iniciar Sesión", fontWeight = FontWeight.Bold)
                        }
                    }
                }
            } else if (isB2BMode && !isVerificadoNivel3) {
                // 🛑 BLOQUEO TRUST & SAFETY PARA B2B
                Card(
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(18.dp),
                    colors = CardDefaults.cardColors(containerColor = Color(0xFFFFF3E0)),
                    elevation = CardDefaults.cardElevation(2.dp)
                ) {
                    Column(
                        modifier = Modifier.padding(20.dp),
                        verticalArrangement = Arrangement.spacedBy(12.dp),
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        Icon(Icons.Default.VerifiedUser, null, tint = Color(0xFFE65100), modifier = Modifier.size(48.dp))
                        Text(
                            text = "Verificación de Matrícula Requerida (Nivel 3)",
                            fontWeight = FontWeight.Bold,
                            color = Color(0xFFE65100),
                            textAlign = TextAlign.Center,
                            style = MaterialTheme.typography.titleMedium
                        )
                        Text(
                            text = "Para publicar corresponsalías, derivaciones o interconsultas en la Red de Colegas B2B debes contar con la matrícula profesional verificada por nuestro equipo técnico.",
                            style = MaterialTheme.typography.bodyMedium,
                            textAlign = TextAlign.Center,
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                        Button(
                            onClick = { navController.navigate("onboarding_rol?role=abogado") },
                            colors = ButtonDefaults.buttonColors(containerColor = Color(0xFFE65100)),
                            shape = RoundedCornerShape(12.dp),
                            modifier = Modifier.fillMaxWidth()
                        ) {
                            Text("Completar Perfil Profesional", fontWeight = FontWeight.Bold)
                        }
                    }
                }
            } else {
                // 📝 FORMULARIO DE PUBLICACIÓN (B2C o B2B)
                if (isB2BMode) {
                    // Controles específicos B2B
                    MktSeccionLabel("Tipo de Solicitud B2B", Icons.Default.Handshake)
                    FlowRow(
                        horizontalArrangement = Arrangement.spacedBy(8.dp),
                        verticalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        ListaSubtiposB2B.forEach { item ->
                            FilterChip(
                                selected = subtipoB2B == item,
                                onClick = { subtipoB2B = item },
                                label = { Text(item, fontWeight = FontWeight.Bold) },
                                colors = FilterChipDefaults.filterChipColors(
                                    selectedContainerColor = AzulB2B,
                                    selectedLabelColor = Color.White
                                )
                            )
                        }
                    }

                    MktSeccionLabel("Jurisdicción / Localidad Destino", Icons.Default.Place)
                    OutlinedTextField(
                        value = jurisdiccionDestino,
                        onValueChange = { jurisdiccionDestino = it },
                        placeholder = { Text("Ej: Santa Fe Capital, Venado Tuerto, Reconquista") },
                        modifier = Modifier.fillMaxWidth(),
                        shape = RoundedCornerShape(14.dp),
                        singleLine = true
                    )

                    MktSeccionLabel("Modalidad de Honorarios / Convenio", Icons.Default.Payments)
                    FlowRow(
                        horizontalArrangement = Arrangement.spacedBy(8.dp),
                        verticalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        ListaModalidadesHonorarios.forEach { item ->
                            FilterChip(
                                selected = modalidadHonorarios == item,
                                onClick = { modalidadHonorarios = item },
                                label = { Text(item) },
                                colors = FilterChipDefaults.filterChipColors(
                                    selectedContainerColor = VerdeAbogado,
                                    selectedLabelColor = Color.White
                                )
                            )
                        }
                    }

                    MktSeccionLabel("Detalle del Requerimiento entre Colegas", Icons.Default.Description)
                    OutlinedTextField(
                        value = descripcion,
                        onValueChange = { descripcion = it },
                        placeholder = { Text("Describí los detalles de la diligencia, audiencia, expediente o trámite a corresponsalar...") },
                        modifier = Modifier.fillMaxWidth().height(130.dp),
                        shape = RoundedCornerShape(14.dp),
                        maxLines = 6
                    )
                } else {
                    // Formulario Estándar B2C
                    MktSeccionLabel("Describí tu caso", Icons.Default.Description)
                    OutlinedTextField(
                        value = descripcion,
                        onValueChange = { descripcion = it },
                        placeholder = { Text("Contanos brevemente tu situación legal...") },
                        modifier = Modifier.fillMaxWidth().height(120.dp),
                        shape = RoundedCornerShape(14.dp),
                        maxLines = 6
                    )

                    MktSeccionLabel("Ubicación", Icons.Default.LocationOn)
                    OutlinedTextField(
                        value = ubicacion,
                        onValueChange = { ubicacion = it },
                        modifier = Modifier.fillMaxWidth(),
                        shape = RoundedCornerShape(14.dp),
                        leadingIcon = { Icon(Icons.Default.Place, null) },
                        singleLine = true
                    )

                    MktSeccionLabel("Teléfono (Opcional)", Icons.Default.Phone)
                    OutlinedTextField(
                        value = telefono,
                        onValueChange = { telefono = it },
                        placeholder = { Text("Ej: 341 1234567") },
                        modifier = Modifier.fillMaxWidth(),
                        shape = RoundedCornerShape(14.dp),
                        leadingIcon = { Icon(Icons.Default.Phone, null) },
                        singleLine = true
                    )
                }

                Spacer(Modifier.height(8.dp))

                Button(
                    onClick = {
                        if (descripcion.isNotBlank()) {
                            feedViewModel.enviarConsultaLegal(
                                descripcion = descripcion,
                                telefono = telefono,
                                ubicacion = ubicacion,
                                urgencia = urgencia,
                                tipoConsulta = if (isB2BMode) "colegiado" else "cliente",
                                subtipoB2B = if (isB2BMode) subtipoB2B else null,
                                jurisdiccionDestino = if (isB2BMode) jurisdiccionDestino else null,
                                modalidadHonorarios = if (isB2BMode) modalidadHonorarios else null
                            )
                            navController.popBackStack()
                        }
                    },
                    modifier = Modifier.fillMaxWidth().height(54.dp),
                    enabled = descripcion.isNotBlank(),
                    colors = ButtonDefaults.buttonColors(containerColor = if (isB2BMode) AzulB2B else AzulCliente),
                    shape = RoundedCornerShape(16.dp)
                ) {
                    Icon(if (isB2BMode) Icons.Default.Handshake else Icons.Default.Send, null, modifier = Modifier.size(18.dp))
                    Spacer(Modifier.width(8.dp))
                    Text(if (isB2BMode) "Publicar en Red de Colegas" else "Publicar Consulta", fontWeight = FontWeight.Bold)
                }
            }

            Spacer(Modifier.height(16.dp))
        }
    }
}
